import { useEffect, useState } from "react";

export type ResolvedTheme = "light" | "dark";

/**
 * Theme is driven by the host environment:
 *   1. Microsoft Teams (when embedded) - via ?theme= URL param on initial load
 *      and the Teams JS SDK theme-change handler for live updates.
 *   2. The OS / browser color-scheme preference, as a fallback.
 *
 * There is no user-facing toggle: the tab matches Teams light/dark/contrast
 * (contrast maps to dark) so it always blends with the surrounding chrome.
 */
export interface UseThemeResult {
  resolved: ResolvedTheme;
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
}

function mapTeamsTheme(theme: string | null | undefined): ResolvedTheme | null {
  if (!theme) return null;
  const normalized = theme.toLowerCase();
  if (normalized === "dark" || normalized === "contrast") return "dark";
  if (normalized === "default" || normalized === "light") return "light";
  return null;
}

function readUrlTheme(): ResolvedTheme | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return mapTeamsTheme(params.get("theme"));
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function initialResolved(): ResolvedTheme {
  return readUrlTheme() ?? (systemPrefersDark() ? "dark" : "light");
}

export function useTheme(): UseThemeResult {
  const [resolved, setResolved] = useState<ResolvedTheme>(() => initialResolved());

  // Keep <html data-theme> in sync.
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // Follow system theme changes (used when not in Teams, or before Teams init resolves).
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      // Only override from system if Teams did not pin a theme via URL.
      if (readUrlTheme() != null) return;
      setResolved(media.matches ? "dark" : "light");
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  // Try to initialize Teams JS and register a theme-change handler. This is a
  // no-op (with a quick rejected promise) when the app is loaded outside Teams.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const teams = await import("@microsoft/teams-js");
        await teams.app.initialize();
        if (cancelled) return;
        // Tell Teams to dismiss its loading screen and show the tab content.
        teams.app.notifyAppLoaded();
        teams.app.notifySuccess();
        const ctx = await teams.app.getContext();
        const initial = mapTeamsTheme(ctx.app?.theme);
        if (initial && !cancelled) setResolved(initial);
        teams.app.registerOnThemeChangeHandler((next: string) => {
          const mapped = mapTeamsTheme(next);
          if (mapped) setResolved(mapped);
        });
      } catch {
        /* Not running inside Teams - rely on URL/system theme. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { resolved };
}
